import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { carrierContactSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import * as carriersActionCreators from '../carriers/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { CarrierContact } from './model'
import { Carrier } from '../carriers/model'

// Fetch

const fetchCarrierContactsEpic = (action$, store) => {
    const tableIdentifier = 'carrier_contacts_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `carrier-contacts/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(carrierContactSchema)
                        )

                        const carrierContacts = normalizedEntitiesToRecordMap(
                            normalizedData.entities.carrier_contacts,
                            CarrierContact,
                            normalizedData.result
                        )
                        const carriers = normalizedEntitiesToRecordMap(normalizedData.entities.carriers, Carrier)

                        const observables = [
                            Observable.of(carriersActionCreators.fetchCarriersFulfilled(carriers)),
                            Observable.of(actionCreators.fetchCarrierContactsFulfilled(carrierContacts)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchCarrierContactsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchCarrierContactEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().carrierContacts.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `carrier-contacts/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, carrierContactSchema)

                        const carrierContacts = normalizedEntitiesToRecordMap(normalizedData.entities.carrier_contacts, CarrierContact)
                        const carriers = normalizedEntitiesToRecordMap(normalizedData.entities.carriers, Carrier)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchCarrierContactsFulfilled(carrierContacts)),
                            Observable.of(actionCreators.fetchCarrierContactFulfilled()),
                            Observable.of(carriersActionCreators.fetchCarriersFulfilled(carriers))
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchCarrierContactsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveCarrierContactEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().carrierContacts.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload.values)
            if (action.payload.ignoreDuplicity) {
                values.ignore_duplicity = 1
            }

            let path = `carrier-contacts`
            let method = 'POST'

            if (values.id) {
                path = `carrier-contacts/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        if (ajaxResponse.response.duplicity_found) {
                            return Observable.concat(Observable.of(actionCreators.saveCarrierContactDuplicityFound(ajaxResponse.response.duplicity)))
                        }

                        const normalizedData = normalize(ajaxResponse.response, carrierContactSchema)

                        const carrierContacts = normalizedEntitiesToRecordMap(normalizedData.entities.carrier_contacts, CarrierContact)
                        const carrierContact = carrierContacts.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveCarrierContactFulfilled(carrierContact)))
                    })
                    .catch(error => Observable.of(actionCreators.saveCarrierContactRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteCarrierContactEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().carrierContacts.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `carrier-contacts/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteCarrierContactFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteCarrierContactRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportCarrierContactsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `carrier-contacts/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [fetchCarrierContactsEpic, fetchCarrierContactEpic, saveCarrierContactEpic, deleteCarrierContactEpic, exportCarrierContactsEpic]
